import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Header } from './components/Header'
import './App.css'

// Time saved per action (minutes saved vs manual process)
const TIME_SAVED_PER_CALIBRATION = 12
const TIME_SAVED_PER_TASK = 8
const TIME_SAVED_PER_FEEDBACK = 5
const TIME_SAVED_PER_RECOGNITION = 3

// Types for Talent Management
export interface Employee {
  id: string
  name: string
  role: string
  department: string
  tenure: string
  performanceRating: 'exceeds' | 'meets' | 'developing' | 'needs_improvement'
  calibrationBucket: CalibrationBucket | null
  promotionReady: boolean
  reviewStatus: 'not_started' | 'self_review' | 'manager_review' | 'calibration' | 'complete'
  signals: TalentSignal[]
  lastPromotion: string
  // Extended profile fields
  skills: string[]
  developmentGoals: string[]
  pastReviews: PastReview[]
  manager: string
  directReports: number
  feedbackReceived: number
  recognitionsGiven: number
}

export interface PastReview {
  period: string
  rating: string
  summary: string
}

export type CalibrationBucket = 'top_talent' | 'strong_performer' | 'solid_contributor' | 'needs_development'

export interface TalentSignal {
  type: 'strength' | 'growth_area' | 'flight_risk' | 'promotion_ready'
  label: string
}

export interface ReviewTask {
  id: string
  type: 'self_review' | 'manager_review' | 'calibration' | 'feedback_request'
  employeeName: string
  dueDate: string
  status: 'pending' | 'in_progress' | 'complete'
}

// Organization data for multi-org scaling demo
export interface OrgUnit {
  id: string
  name: string
  headcount: number
  calibrationProgress: number
  reviewsComplete: number
  flightRiskCount: number
}

const INITIAL_EMPLOYEES: Employee[] = [
  {
    id: '1',
    name: 'Alex Chen',
    role: 'Senior Engineer',
    department: 'Platform',
    tenure: '2.5 years',
    performanceRating: 'exceeds',
    calibrationBucket: null,
    promotionReady: true,
    reviewStatus: 'calibration',
    lastPromotion: '18 months ago',
    signals: [
      { type: 'promotion_ready', label: 'Ready for Staff Engineer' },
      { type: 'strength', label: 'Consistently delivers complex projects' }
    ],
    skills: ['System Design', 'Go', 'Distributed Systems', 'Mentorship'],
    developmentGoals: ['Lead cross-team initiatives', 'Publish engineering blog post', 'Present at internal tech talks'],
    pastReviews: [
      { period: 'H2 2024', rating: 'Exceeds', summary: 'Led platform migration with zero downtime. Mentored 2 junior engineers.' },
      { period: 'H1 2024', rating: 'Exceeds', summary: 'Delivered payment service refactor 2 weeks early.' }
    ],
    manager: 'Casey Brown',
    directReports: 0,
    feedbackReceived: 12,
    recognitionsGiven: 8
  },
  {
    id: '2',
    name: 'Sarah Miller',
    role: 'Product Manager',
    department: 'Growth',
    tenure: '1.5 years',
    performanceRating: 'meets',
    calibrationBucket: null,
    promotionReady: false,
    reviewStatus: 'manager_review',
    lastPromotion: '6 months ago',
    signals: [
      { type: 'strength', label: 'Strong cross-functional collaboration' },
      { type: 'growth_area', label: 'Developing technical depth' }
    ],
    skills: ['Product Strategy', 'User Research', 'Roadmapping', 'Stakeholder Management'],
    developmentGoals: ['Complete SQL certification', 'Shadow engineering sprints', 'Lead quarterly planning'],
    pastReviews: [
      { period: 'H2 2024', rating: 'Meets', summary: 'Successfully launched 3 experiments. Could improve technical specifications.' },
      { period: 'H1 2024', rating: 'Meets', summary: 'Strong user research driving roadmap decisions.' }
    ],
    manager: 'David Kim',
    directReports: 0,
    feedbackReceived: 8,
    recognitionsGiven: 15
  },
  {
    id: '3',
    name: 'Jordan Lee',
    role: 'Data Scientist',
    department: 'Analytics',
    tenure: '3 years',
    performanceRating: 'exceeds',
    calibrationBucket: null,
    promotionReady: true,
    reviewStatus: 'calibration',
    lastPromotion: '24 months ago',
    signals: [
      { type: 'promotion_ready', label: 'Overdue for Senior DS' },
      { type: 'flight_risk', label: 'Market demand is high' }
    ],
    skills: ['Python', 'ML/AI', 'Statistical Modeling', 'A/B Testing', 'Data Visualization'],
    developmentGoals: ['Deploy ML model to production', 'Present at industry conference', 'Build team data literacy'],
    pastReviews: [
      { period: 'H2 2024', rating: 'Exceeds', summary: 'ML model improved conversion by 12%. Built self-serve analytics dashboard.' },
      { period: 'H1 2024', rating: 'Exceeds', summary: 'Automated reporting saving 20 hours/week. Received 3 LinkedIn recruiter contacts.' }
    ],
    manager: 'Lisa Wang',
    directReports: 0,
    feedbackReceived: 6,
    recognitionsGiven: 4
  },
  {
    id: '4',
    name: 'Morgan Taylor',
    role: 'UX Designer',
    department: 'Design',
    tenure: '1 year',
    performanceRating: 'meets',
    calibrationBucket: null,
    promotionReady: false,
    reviewStatus: 'self_review',
    lastPromotion: 'N/A (new hire)',
    signals: [
      { type: 'strength', label: 'Exceptional user empathy' },
      { type: 'growth_area', label: 'Building influence with stakeholders' }
    ],
    skills: ['Figma', 'User Research', 'Prototyping', 'Design Systems'],
    developmentGoals: ['Lead design sprint', 'Build component library', 'Present to executive team'],
    pastReviews: [
      { period: 'H2 2024', rating: 'Meets', summary: 'Strong first year. Redesigned onboarding flow with 15% improvement.' }
    ],
    manager: 'Jennifer Park',
    directReports: 0,
    feedbackReceived: 5,
    recognitionsGiven: 10
  },
  {
    id: '5',
    name: 'Casey Brown',
    role: 'Engineering Manager',
    department: 'Infrastructure',
    tenure: '4 years',
    performanceRating: 'exceeds',
    calibrationBucket: null,
    promotionReady: true,
    reviewStatus: 'complete',
    lastPromotion: '12 months ago',
    signals: [
      { type: 'promotion_ready', label: 'Director track candidate' },
      { type: 'strength', label: 'Team has lowest attrition' }
    ],
    skills: ['People Management', 'Technical Architecture', 'Strategic Planning', 'Cross-functional Leadership'],
    developmentGoals: ['Expand scope to 2nd team', 'Executive presence development', 'Build succession pipeline'],
    pastReviews: [
      { period: 'H2 2024', rating: 'Exceeds', summary: 'Team delivered 120% of roadmap. Zero regrettable attrition. Promoted 2 ICs.' },
      { period: 'H1 2024', rating: 'Exceeds', summary: 'Successfully onboarded 4 new engineers. Improved team velocity by 30%.' }
    ],
    manager: 'VP Engineering',
    directReports: 6,
    feedbackReceived: 18,
    recognitionsGiven: 25
  },
]

// Multi-org data for scaling visualization
const INITIAL_ORGS: OrgUnit[] = [
  { id: '1', name: 'Engineering', headcount: 450, calibrationProgress: 78, reviewsComplete: 65, flightRiskCount: 12 },
  { id: '2', name: 'Product', headcount: 120, calibrationProgress: 92, reviewsComplete: 88, flightRiskCount: 3 },
  { id: '3', name: 'Design', headcount: 80, calibrationProgress: 85, reviewsComplete: 72, flightRiskCount: 2 },
  { id: '4', name: 'Data Science', headcount: 65, calibrationProgress: 70, reviewsComplete: 55, flightRiskCount: 5 },
  { id: '5', name: 'Operations', headcount: 200, calibrationProgress: 60, reviewsComplete: 45, flightRiskCount: 8 },
]

const INITIAL_TASKS: ReviewTask[] = [
  { id: '1', type: 'calibration', employeeName: 'Alex Chen', dueDate: 'Today', status: 'pending' },
  { id: '2', type: 'manager_review', employeeName: 'Sarah Miller', dueDate: 'Tomorrow', status: 'in_progress' },
  { id: '3', type: 'calibration', employeeName: 'Jordan Lee', dueDate: 'Today', status: 'pending' },
  { id: '4', type: 'feedback_request', employeeName: 'Morgan Taylor', dueDate: 'In 3 days', status: 'pending' },
]

const CALIBRATION_BUCKETS: { key: CalibrationBucket; label: string; color: string; description: string }[] = [
  { key: 'top_talent', label: 'Top Talent', color: '#22C55E', description: 'Exceptional impact, high potential' },
  { key: 'strong_performer', label: 'Strong Performer', color: '#3B82F6', description: 'Consistently exceeds expectations' },
  { key: 'solid_contributor', label: 'Solid Contributor', color: '#F59E0B', description: 'Meets expectations reliably' },
  { key: 'needs_development', label: 'Needs Development', color: '#EF4444', description: 'Requires focused support' },
]

function App() {
  const [employees, setEmployees] = useState<Employee[]>(INITIAL_EMPLOYEES)
  const [tasks, setTasks] = useState<ReviewTask[]>(INITIAL_TASKS)
  const [orgs] = useState<OrgUnit[]>(INITIAL_ORGS)
  const [isLoaded, setIsLoaded] = useState(false)
  const [activityLog, setActivityLog] = useState<string[]>([])
  const [totalTimeSaved, setTotalTimeSaved] = useState(0)
  const [showCelebration, setShowCelebration] = useState(false)
  const [celebrationMessage, setCelebrationMessage] = useState('')
  const [celebrationSubtext, setCelebrationSubtext] = useState('')
  const [lastAction, setLastAction] = useState<string | null>(null)
  const [showComparison, setShowComparison] = useState(true)
  const [dragOverBucket, setDragOverBucket] = useState<CalibrationBucket | null>(null)
  const [draggingEmployee, setDraggingEmployee] = useState<string | null>(null)
  // New state for added features
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [feedbackTarget, setFeedbackTarget] = useState<Employee | null>(null)
  const [feedbackType, setFeedbackType] = useState<'request' | 'recognition'>('request')
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewTarget, setReviewTarget] = useState<Employee | null>(null)

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  const addActivityLog = (log: string) => {
    setActivityLog(prev => [`[${new Date().toLocaleTimeString()}] ${log}`, ...prev.slice(0, 14)])
  }

  const triggerCelebration = (message: string, subtext: string) => {
    setCelebrationMessage(message)
    setCelebrationSubtext(subtext)
    setShowCelebration(true)
    setTimeout(() => setShowCelebration(false), 2500)
  }

  const assignToBucket = (employeeId: string, bucket: CalibrationBucket) => {
    const employee = employees.find(e => e.id === employeeId)
    if (!employee || employee.calibrationBucket !== null) return

    const bucketInfo = CALIBRATION_BUCKETS.find(b => b.key === bucket)

    setEmployees(prev => prev.map(emp =>
      emp.id === employeeId ? { ...emp, calibrationBucket: bucket } : emp
    ))

    setTotalTimeSaved(prev => prev + TIME_SAVED_PER_CALIBRATION)
    setLastAction(`+${TIME_SAVED_PER_CALIBRATION} min saved`)
    setTimeout(() => setLastAction(null), 2000)

    addActivityLog(`${employee.name} calibrated as "${bucketInfo?.label}" (saved ${TIME_SAVED_PER_CALIBRATION} min)`)

    // Hide comparison after first interaction
    setShowComparison(false)

    // Special celebrations
    if (employee.signals.some(s => s.type === 'flight_risk')) {
      triggerCelebration('Flight Risk Addressed!', `${employee.name} is now calibrated. Consider retention conversation.`)
    } else if (bucket === 'top_talent') {
      triggerCelebration('Top Talent Identified!', `${employee.name} flagged for accelerated development.`)
    }

    // Check if all employees are calibrated
    const calibratedCount = employees.filter(e => e.calibrationBucket !== null).length + 1
    if (calibratedCount === employees.length) {
      setTimeout(() => {
        triggerCelebration('All Calibrations Complete!', `Team calibrated in record time. Ready for review meeting.`)
      }, 500)
    }
  }

  const completeTask = (taskId: string) => {
    const task = tasks.find(t => t.id === taskId)
    if (!task || task.status === 'complete') return

    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, status: 'complete' as const } : t
    ))

    setTotalTimeSaved(prev => prev + TIME_SAVED_PER_TASK)
    setLastAction(`+${TIME_SAVED_PER_TASK} min saved`)
    setTimeout(() => setLastAction(null), 2000)

    addActivityLog(`Completed: ${task.type.replace(/_/g, ' ')} for ${task.employeeName}`)

    // Check if all tasks complete
    const completedCount = tasks.filter(t => t.status === 'complete').length + 1
    if (completedCount === tasks.length) {
      triggerCelebration('All Tasks Complete!', 'Review cycle tasks finished ahead of schedule.')
    }
  }

  const promoteEmployee = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId)
    if (!employee || !employee.promotionReady || employee.calibrationBucket !== 'top_talent') return

    setEmployees(prev => prev.map(emp =>
      emp.id === employeeId ? {
        ...emp,
        promotionReady: false,
        signals: emp.signals.filter(s => s.type !== 'promotion_ready')
      } : emp
    ))

    setTotalTimeSaved(prev => prev + 30) // Promotion workflow saves 30 min
    setLastAction('+30 min saved')
    setTimeout(() => setLastAction(null), 2000)

    addActivityLog(`Promotion initiated for ${employee.name} - workflow triggered`)
    triggerCelebration('Promotion Initiated!', `${employee.name}'s promotion packet has been generated.`)
  }

  const requestFeedback = (employee: Employee) => {
    setFeedbackTarget(employee)
    setFeedbackType('request')
    setShowFeedbackModal(true)
  }

  const giveRecognition = (employee: Employee) => {
    setFeedbackTarget(employee)
    setFeedbackType('recognition')
    setShowFeedbackModal(true)
  }

  const submitFeedback = () => {
    if (!feedbackTarget) return

    const timeSaved = feedbackType === 'request' ? TIME_SAVED_PER_FEEDBACK : TIME_SAVED_PER_RECOGNITION
    setTotalTimeSaved(prev => prev + timeSaved)
    setLastAction(`+${timeSaved} min saved`)
    setTimeout(() => setLastAction(null), 2000)

    setEmployees(prev => prev.map(emp =>
      emp.id === feedbackTarget.id ? {
        ...emp,
        feedbackReceived: emp.feedbackReceived + (feedbackType === 'request' ? 1 : 0),
        recognitionsGiven: emp.recognitionsGiven + (feedbackType === 'recognition' ? 1 : 0)
      } : emp
    ))

    const actionType = feedbackType === 'request' ? 'Feedback requested for' : 'Recognition sent to'
    addActivityLog(`${actionType} ${feedbackTarget.name}`)

    if (feedbackType === 'recognition') {
      triggerCelebration('Recognition Sent!', `${feedbackTarget.name} will be notified of your appreciation.`)
    } else {
      triggerCelebration('Feedback Requested!', `Request sent to ${feedbackTarget.name}'s peers and stakeholders.`)
    }

    setShowFeedbackModal(false)
    setFeedbackTarget(null)
  }

  const openReview = (employee: Employee) => {
    setReviewTarget(employee)
    setShowReviewModal(true)
  }

  const submitReview = () => {
    if (!reviewTarget) return

    setEmployees(prev => prev.map(emp =>
      emp.id === reviewTarget.id ? {
        ...emp,
        reviewStatus: 'complete' as const
      } : emp
    ))

    setTotalTimeSaved(prev => prev + 15) // Review submission saves 15 min
    setLastAction('+15 min saved')
    setTimeout(() => setLastAction(null), 2000)

    addActivityLog(`Review submitted for ${reviewTarget.name}`)
    triggerCelebration('Review Submitted!', `${reviewTarget.name}'s review is now complete.`)

    setShowReviewModal(false)
    setReviewTarget(null)
  }

  // Computed metrics
  const calibratedCount = employees.filter(e => e.calibrationBucket !== null).length
  const completedTasks = tasks.filter(t => t.status === 'complete').length
  const promotionReadyCount = employees.filter(e => e.promotionReady && e.calibrationBucket === 'top_talent').length

  const getPerformanceColor = (rating: Employee['performanceRating']) => {
    switch (rating) {
      case 'exceeds': return '#22C55E'
      case 'meets': return '#3B82F6'
      case 'developing': return '#F59E0B'
      case 'needs_improvement': return '#EF4444'
    }
  }

  const getSignalIcon = (type: TalentSignal['type']) => {
    switch (type) {
      case 'promotion_ready': return '⬆️'
      case 'strength': return '💪'
      case 'growth_area': return '🌱'
      case 'flight_risk': return '⚠️'
    }
  }

  // Employees not yet calibrated (shown in roster)
  const uncalibratedEmployees = employees.filter(e => e.calibrationBucket === null)

  return (
    <div className="app">
      <AnimatePresence>
        {isLoaded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Header />

            <main className="main-content">
              {/* Celebration Overlay */}
              <AnimatePresence>
                {showCelebration && (
                  <motion.div
                    className="celebration-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowCelebration(false)}
                  >
                    <motion.div
                      className="celebration-content"
                      initial={{ scale: 0.5, y: 20 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                    >
                      <span className="celebration-emoji">🎯</span>
                      <span className="celebration-text">{celebrationMessage}</span>
                      <span className="celebration-subtext">{celebrationSubtext}</span>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Hero Section */}
              <motion.div
                className="hero-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <div className="hero-badge">
                  <span className="badge-dot" />
                  Live Demo: Built in 2 Hours
                </div>
                <h1 className="hero-title">
                  What if calibrations took
                  <span className="hero-title-accent"> minutes, not weeks?</span>
                </h1>
                <p className="hero-subtitle">
                  Chasing managers for reviews. Reconciling spreadsheets. Inconsistent ratings across orgs.
                  Performance cycles drain 40+ hours from every HR partner.
                  <strong> Drag employees to buckets and watch the magic happen.</strong>
                </p>

                {/* Before/After Comparison */}
                <AnimatePresence>
                  {showComparison && (
                    <motion.div
                      className="comparison-container"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <div className="comparison-card before">
                        <div className="comparison-label">Without This Tool</div>
                        <div className="comparison-items">
                          <span>📊 Export to spreadsheet</span>
                          <span>📧 Email managers for input</span>
                          <span>⏳ Wait 2-3 days for responses</span>
                          <span>🔄 Reconcile conflicting ratings</span>
                          <span>📝 Manual data entry back to HRIS</span>
                        </div>
                        <div className="comparison-time">~45 min per employee</div>
                      </div>
                      <div className="comparison-arrow">→</div>
                      <div className="comparison-card after">
                        <div className="comparison-label">With This Tool</div>
                        <div className="comparison-items">
                          <span>🎯 Drag to calibration bucket</span>
                          <span>✅ Done</span>
                        </div>
                        <div className="comparison-time">~30 seconds per employee</div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Progress Counter */}
                <motion.div
                  className="time-saved-counter"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="time-saved-label">Time Saved This Session</div>
                  <motion.div
                    className="time-saved-value mono"
                    key={totalTimeSaved}
                    initial={{ scale: 1.3, color: '#22C55E' }}
                    animate={{ scale: 1, color: '#FF3008' }}
                    transition={{ duration: 0.4 }}
                  >
                    {totalTimeSaved} minutes
                  </motion.div>
                  <div className="time-saved-equivalent">
                    = {(totalTimeSaved / 60).toFixed(1)} hours of HR partner time
                  </div>
                  <AnimatePresence>
                    {lastAction && (
                      <motion.div
                        className="time-saved-popup"
                        initial={{ opacity: 0, y: 10, scale: 0.8 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20 }}
                      >
                        {lastAction}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>

              {/* Metrics Dashboard */}
              <motion.div
                className="metrics-grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="metric-card">
                  <motion.div
                    className="metric-value"
                    key={calibratedCount}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                  >
                    {calibratedCount}/{employees.length}
                  </motion.div>
                  <div className="metric-label">Calibrated</div>
                  <div className="metric-progress">
                    <div
                      className="metric-progress-bar"
                      style={{ width: `${(calibratedCount / employees.length) * 100}%` }}
                    />
                  </div>
                </div>
                <div className="metric-card">
                  <motion.div
                    className="metric-value"
                    key={completedTasks}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                  >
                    {completedTasks}/{tasks.length}
                  </motion.div>
                  <div className="metric-label">Tasks Done</div>
                  <div className="metric-progress">
                    <div
                      className="metric-progress-bar"
                      style={{ width: `${(completedTasks / tasks.length) * 100}%` }}
                    />
                  </div>
                </div>
                <div className={`metric-card ${promotionReadyCount > 0 ? 'highlight' : ''}`}>
                  <motion.div
                    className="metric-value"
                    key={promotionReadyCount}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                  >
                    {promotionReadyCount}
                  </motion.div>
                  <div className="metric-label">Ready to Promote</div>
                </div>
                <div className="metric-card">
                  <motion.div
                    className="metric-value accent"
                    key={totalTimeSaved}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                  >
                    {totalTimeSaved}m
                  </motion.div>
                  <div className="metric-label">Time Saved</div>
                </div>
              </motion.div>

              {/* Main Content Grid */}
              <div className="tm-grid">
                {/* Left: Calibration Buckets */}
                <motion.div
                  className="calibration-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  <h2 className="section-title">Calibration Buckets</h2>
                  <p className="section-subtitle">Drag employees from the roster on the right →</p>

                  {uncalibratedEmployees.length > 0 && calibratedCount === 0 && (
                    <motion.div
                      className="first-action-hint"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1 }}
                    >
                      👆 Try it: Drag "Jordan Lee" (flight risk!) to Top Talent
                    </motion.div>
                  )}

                  <div className="calibration-buckets">
                    {CALIBRATION_BUCKETS.map(bucket => {
                      const bucketEmployees = employees.filter(e => e.calibrationBucket === bucket.key)
                      return (
                        <div
                          key={bucket.key}
                          className={`calibration-bucket ${dragOverBucket === bucket.key ? 'drag-over' : ''}`}
                          onDragOver={(e) => {
                            e.preventDefault()
                            setDragOverBucket(bucket.key)
                          }}
                          onDragLeave={() => setDragOverBucket(null)}
                          onDrop={(e) => {
                            e.preventDefault()
                            const employeeId = e.dataTransfer.getData('employeeId')
                            if (employeeId) assignToBucket(employeeId, bucket.key)
                            setDragOverBucket(null)
                            setDraggingEmployee(null)
                          }}
                        >
                          <div className="bucket-header" style={{ borderLeftColor: bucket.color }}>
                            <span className="bucket-label">{bucket.label}</span>
                            <span className="bucket-count" style={{ backgroundColor: bucketEmployees.length > 0 ? bucket.color : undefined }}>
                              {bucketEmployees.length}
                            </span>
                          </div>
                          <p className="bucket-description">{bucket.description}</p>
                          <div className="bucket-employees">
                            <AnimatePresence>
                              {bucketEmployees.map(emp => (
                                <motion.div
                                  key={emp.id}
                                  className="bucket-employee"
                                  style={{ borderLeftColor: bucket.color }}
                                  initial={{ opacity: 0, x: -20, scale: 0.8 }}
                                  animate={{ opacity: 1, x: 0, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.8 }}
                                  layout
                                >
                                  <div className="bucket-emp-info">
                                    <span className="emp-name">{emp.name}</span>
                                    <span className="emp-role">{emp.role}</span>
                                  </div>
                                  {emp.promotionReady && bucket.key === 'top_talent' && (
                                    <button
                                      className="promote-btn"
                                      onClick={() => promoteEmployee(emp.id)}
                                    >
                                      Promote
                                    </button>
                                  )}
                                </motion.div>
                              ))}
                            </AnimatePresence>
                            {bucketEmployees.length === 0 && (
                              <div className="bucket-empty">
                                Drop employees here
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </motion.div>

                {/* Right Column */}
                <div className="right-column">
                  {/* Team Roster - Only uncalibrated employees */}
                  <motion.div
                    className="team-roster"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    <h2 className="section-title">
                      Pending Calibration
                      <span className="roster-count">({uncalibratedEmployees.length})</span>
                    </h2>
                    <div className="employee-list">
                      <AnimatePresence>
                        {uncalibratedEmployees.map(emp => {
                          const hasFlightRisk = emp.signals.some(s => s.type === 'flight_risk')
                          const isPromotionReady = emp.promotionReady
                          return (
                            <motion.div
                              key={emp.id}
                              initial={{ opacity: 1 }}
                              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                              layout
                            >
                              <div
                                className={`employee-card ${draggingEmployee === emp.id ? 'dragging' : ''} ${hasFlightRisk ? 'flight-risk' : ''} ${isPromotionReady ? 'promotion-ready' : ''}`}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('employeeId', emp.id)
                                  e.dataTransfer.effectAllowed = 'move'
                                  setDraggingEmployee(emp.id)
                                }}
                                onDragEnd={() => setDraggingEmployee(null)}
                              >
                                <div className="emp-header">
                                  <div className="emp-info" onClick={() => setSelectedEmployee(emp)} style={{ cursor: 'pointer' }}>
                                    <span className="emp-name">{emp.name}</span>
                                    <span className="emp-role">{emp.role} · {emp.department}</span>
                                  </div>
                                  <div
                                    className="emp-rating"
                                    style={{ backgroundColor: getPerformanceColor(emp.performanceRating) }}
                                  >
                                    {emp.performanceRating.replace('_', ' ')}
                                  </div>
                                </div>
                                <div className="emp-signals">
                                  {emp.signals.map((signal, i) => (
                                    <span key={i} className={`signal signal-${signal.type}`}>
                                      {getSignalIcon(signal.type)} {signal.label}
                                    </span>
                                  ))}
                                </div>
                                <div className="emp-actions">
                                  <button className="emp-action-btn" onClick={(e) => { e.stopPropagation(); requestFeedback(emp); }}>
                                    Request Feedback
                                  </button>
                                  <button className="emp-action-btn recognition" onClick={(e) => { e.stopPropagation(); giveRecognition(emp); }}>
                                    Recognize
                                  </button>
                                </div>
                                {hasFlightRisk && (
                                  <div className="flight-risk-badge">
                                    ⚠️ Flight Risk - Calibrate Now
                                  </div>
                                )}
                                <div className="drag-hint">⋮⋮ Drag to bucket | Click name for full profile</div>
                              </div>
                            </motion.div>
                          )
                        })}
                      </AnimatePresence>
                      {uncalibratedEmployees.length === 0 && (
                        <div className="roster-empty">
                          <span className="roster-empty-icon">✅</span>
                          <span>All employees calibrated!</span>
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Task Queue */}
                  <motion.div
                    className="task-queue"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.7 }}
                  >
                    <h2 className="section-title">Review Tasks</h2>
                    <div className="task-list">
                      {tasks.map(task => (
                        <motion.div
                          key={task.id}
                          className={`task-item ${task.status}`}
                          layout
                        >
                          <div className="task-info">
                            <span className="task-type">{task.type.replace(/_/g, ' ')}</span>
                            <span className="task-employee">{task.employeeName}</span>
                            <span className="task-due">Due: {task.dueDate}</span>
                          </div>
                          {task.status !== 'complete' ? (
                            <button
                              className="task-complete-btn"
                              onClick={() => completeTask(task.id)}
                            >
                              Complete
                            </button>
                          ) : (
                            <motion.span
                              className="task-done"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                            >
                              ✓
                            </motion.span>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Multi-Org Scaling Visualization */}
              <motion.div
                className="multi-org-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.75 }}
              >
                <h2 className="section-title">Multi-Org View: Scales Across Business Units</h2>
                <p className="section-subtitle">Real-time analytics dashboard across 915 employees in 5 organizations</p>
                <div className="org-grid">
                  {orgs.map(org => (
                    <div key={org.id} className="org-card">
                      <div className="org-header">
                        <span className="org-name">{org.name}</span>
                        <span className="org-headcount">{org.headcount} employees</span>
                      </div>
                      <div className="org-metrics">
                        <div className="org-metric">
                          <span className="org-metric-label">Calibration</span>
                          <div className="org-progress-bar">
                            <div
                              className="org-progress-fill"
                              style={{ width: `${org.calibrationProgress}%` }}
                            />
                          </div>
                          <span className="org-metric-value">{org.calibrationProgress}%</span>
                        </div>
                        <div className="org-metric">
                          <span className="org-metric-label">Reviews Done</span>
                          <div className="org-progress-bar">
                            <div
                              className="org-progress-fill reviews"
                              style={{ width: `${org.reviewsComplete}%` }}
                            />
                          </div>
                          <span className="org-metric-value">{org.reviewsComplete}%</span>
                        </div>
                        {org.flightRiskCount > 0 && (
                          <div className="org-flight-risk">
                            <span className="flight-risk-icon">⚠️</span>
                            <span>{org.flightRiskCount} flight risks</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="org-total">
                  <span className="org-total-label">Total Progress:</span>
                  <span className="org-total-value">
                    {Math.round(orgs.reduce((acc, org) => acc + org.calibrationProgress * org.headcount, 0) / orgs.reduce((acc, org) => acc + org.headcount, 0))}% calibrated across all orgs
                  </span>
                </div>
              </motion.div>

              {/* Connected Insights Section */}
              <motion.div
                className="insights-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.78 }}
              >
                <h2 className="section-title">Analytics Dashboard: Connected Insights</h2>
                <p className="section-subtitle">Real-time analytics across Greenhouse, HRIS, and performance systems</p>
                <div className="insights-grid">
                  <div className="insight-card">
                    <div className="insight-icon">🔗</div>
                    <div className="insight-content">
                      <span className="insight-title">Data Quality Score</span>
                      <span className="insight-value">94%</span>
                      <span className="insight-detail">All employee records synced and validated</span>
                    </div>
                  </div>
                  <div className="insight-card">
                    <div className="insight-icon">📊</div>
                    <div className="insight-content">
                      <span className="insight-title">Calibration Consistency</span>
                      <span className="insight-value">87%</span>
                      <span className="insight-detail">Cross-manager rating alignment improved 23%</span>
                    </div>
                  </div>
                  <div className="insight-card highlight">
                    <div className="insight-icon">⚡</div>
                    <div className="insight-content">
                      <span className="insight-title">Time to Calibrate</span>
                      <span className="insight-value">2.3 days</span>
                      <span className="insight-detail">Down from 14 days (84% reduction)</span>
                    </div>
                  </div>
                  <div className="insight-card">
                    <div className="insight-icon">🎯</div>
                    <div className="insight-content">
                      <span className="insight-title">Promotion Pipeline</span>
                      <span className="insight-value">42 ready</span>
                      <span className="insight-detail">Identified via ML signals from performance data</span>
                    </div>
                  </div>
                </div>
                <div className="insights-integration">
                  <span className="integration-label">Integrated Systems:</span>
                  <div className="integration-badges">
                    <span className="integration-badge">Greenhouse ATS</span>
                    <span className="integration-badge">Workday HRIS</span>
                    <span className="integration-badge">15Five</span>
                    <span className="integration-badge">Slack</span>
                    <span className="integration-badge">Custom APIs</span>
                  </div>
                </div>
              </motion.div>

              {/* Activity Log */}
              <motion.div
                className="activity-log"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <h2 className="section-title">Activity Log</h2>
                <div className="log-entries">
                  {activityLog.length === 0 ? (
                    <p className="log-empty">Actions will appear here as you calibrate employees and complete tasks</p>
                  ) : (
                    activityLog.map((log, i) => (
                      <motion.div
                        key={i}
                        className="log-entry"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                      >
                        {log}
                      </motion.div>
                    ))
                  )}
                </div>
              </motion.div>

              {/* About Section */}
              <motion.div
                className="about-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
              >
                <div className="about-content">
                  <h2>Why I Built This</h2>

                  <div className="job-match-grid">
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">Calibration workflows that scale across orgs</span>
                    </div>
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">Analytics dashboards with talent signals</span>
                    </div>
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">Task orchestration for review cycles</span>
                    </div>
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">360° feedback and recognition flows</span>
                    </div>
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">Employee profiles with review history</span>
                    </div>
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">Multi-org scaling visualization</span>
                    </div>
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">Connected insights across systems</span>
                    </div>
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">AI-assisted review writing</span>
                    </div>
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">Modular component architecture</span>
                    </div>
                    <div className="job-match-item">
                      <span className="match-icon">✓</span>
                      <span className="match-text">User experience design focus</span>
                    </div>
                  </div>

                  <div className="architecture-section">
                    <h4 className="architecture-title">Built With</h4>
                    <div className="architecture-badges">
                      <span className="arch-badge">SaaS Platform</span>
                      <span className="arch-badge">Modular Components</span>
                      <span className="arch-badge">Engineering Design</span>
                      <span className="arch-badge">Analytics-Driven UX</span>
                    </div>
                  </div>

                  <p>
                    I've managed teams. I've sat through calibration sessions with spreadsheets
                    flying between managers, inconsistent ratings, and no clear signals for who's
                    actually ready for promotion. The pain is real.
                  </p>
                  <p>
                    This demo shows what's possible when you build tools that managers actually want
                    to use: drag-and-drop calibration, surfaced talent signals, task orchestration
                    that doesn't require chasing people in Slack.
                  </p>
                  <p>
                    <em>Traditional PMs manage products. I build them.</em> 2,938 GitHub contributions
                    in 2025. For a People Tech team using a hybrid build+buy approach, that technical
                    fluency isn't nice-to-have. It's how you ship faster.
                  </p>

                  <div className="cta-urgency">
                    <span className="urgency-line">This demo took 2 hours. Imagine what I could build in a quarter.</span>
                  </div>

                  <div className="cta-scarcity">
                    Currently interviewing. Let's talk before my calendar fills up.
                  </div>

                  <div className="about-cta">
                    <a href="mailto:elijahbowie@gmail.com?subject=DoorDash%20People%20Product%20PM%20Role&body=Hi%20Elijah%2C%0A%0AI%20saw%20your%20talent%20management%20demo%20and%20would%20like%20to%20discuss%20the%20Staff%20PM%20role." className="cta-primary">
                      Let's Talk This Week
                    </a>
                    <a
                      href="https://github.com/elijahbowie"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cta-secondary"
                    >
                      View 2,938 Commits
                    </a>
                  </div>
                </div>
              </motion.div>
            </main>

            {/* Employee Profile Modal */}
            <AnimatePresence>
              {selectedEmployee && (
                <motion.div
                  className="modal-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setSelectedEmployee(null)}
                >
                  <motion.div
                    className="modal-content profile-modal"
                    initial={{ scale: 0.9, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className="modal-close" onClick={() => setSelectedEmployee(null)}>×</button>

                    <div className="profile-header">
                      <div className="profile-avatar">
                        {selectedEmployee.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="profile-info">
                        <h2 className="profile-name">{selectedEmployee.name}</h2>
                        <p className="profile-role">{selectedEmployee.role} · {selectedEmployee.department}</p>
                        <p className="profile-meta">Reports to: {selectedEmployee.manager} · Tenure: {selectedEmployee.tenure}</p>
                      </div>
                      <div
                        className="profile-rating"
                        style={{ backgroundColor: getPerformanceColor(selectedEmployee.performanceRating) }}
                      >
                        {selectedEmployee.performanceRating.replace('_', ' ')}
                      </div>
                    </div>

                    <div className="profile-grid">
                      <div className="profile-section">
                        <h3>Talent Signals</h3>
                        <div className="profile-signals">
                          {selectedEmployee.signals.map((signal, i) => (
                            <span key={i} className={`signal signal-${signal.type}`}>
                              {getSignalIcon(signal.type)} {signal.label}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="profile-section">
                        <h3>Skills</h3>
                        <div className="profile-skills">
                          {selectedEmployee.skills.map((skill, i) => (
                            <span key={i} className="skill-badge">{skill}</span>
                          ))}
                        </div>
                      </div>

                      <div className="profile-section">
                        <h3>Development Goals</h3>
                        <ul className="profile-goals">
                          {selectedEmployee.developmentGoals.map((goal, i) => (
                            <li key={i}>{goal}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="profile-section full-width">
                        <h3>Review History</h3>
                        <div className="review-history">
                          {selectedEmployee.pastReviews.map((review, i) => (
                            <div key={i} className="review-item">
                              <div className="review-header">
                                <span className="review-period">{review.period}</span>
                                <span className={`review-rating ${review.rating.toLowerCase()}`}>{review.rating}</span>
                              </div>
                              <p className="review-summary">{review.summary}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="profile-section">
                        <h3>Quick Stats</h3>
                        <div className="profile-stats">
                          <div className="stat-item">
                            <span className="stat-value">{selectedEmployee.feedbackReceived}</span>
                            <span className="stat-label">Feedback Received</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-value">{selectedEmployee.recognitionsGiven}</span>
                            <span className="stat-label">Recognitions Given</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-value">{selectedEmployee.directReports}</span>
                            <span className="stat-label">Direct Reports</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="profile-actions">
                      <button className="action-btn primary" onClick={() => { requestFeedback(selectedEmployee); setSelectedEmployee(null); }}>
                        Request Feedback
                      </button>
                      <button className="action-btn secondary" onClick={() => { giveRecognition(selectedEmployee); setSelectedEmployee(null); }}>
                        Give Recognition
                      </button>
                      {selectedEmployee.reviewStatus !== 'complete' && (
                        <button className="action-btn tertiary" onClick={() => { openReview(selectedEmployee); setSelectedEmployee(null); }}>
                          Complete Review
                        </button>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Feedback Modal */}
            <AnimatePresence>
              {showFeedbackModal && feedbackTarget && (
                <motion.div
                  className="modal-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowFeedbackModal(false)}
                >
                  <motion.div
                    className="modal-content feedback-modal"
                    initial={{ scale: 0.9, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className="modal-close" onClick={() => setShowFeedbackModal(false)}>×</button>

                    <h2 className="modal-title">
                      {feedbackType === 'request' ? 'Request Feedback' : 'Give Recognition'}
                    </h2>
                    <p className="modal-subtitle">
                      {feedbackType === 'request'
                        ? `Request 360° feedback for ${feedbackTarget.name}`
                        : `Recognize ${feedbackTarget.name}'s contributions`}
                    </p>

                    <div className="feedback-form">
                      {feedbackType === 'request' ? (
                        <>
                          <div className="form-group">
                            <label>Request feedback from:</label>
                            <div className="checkbox-group">
                              <label className="checkbox-label">
                                <input type="checkbox" defaultChecked /> Direct peers (3 suggested)
                              </label>
                              <label className="checkbox-label">
                                <input type="checkbox" defaultChecked /> Cross-functional partners (2 suggested)
                              </label>
                              <label className="checkbox-label">
                                <input type="checkbox" /> Skip-level manager
                              </label>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Focus areas:</label>
                            <div className="checkbox-group">
                              <label className="checkbox-label">
                                <input type="checkbox" defaultChecked /> Technical execution
                              </label>
                              <label className="checkbox-label">
                                <input type="checkbox" defaultChecked /> Collaboration
                              </label>
                              <label className="checkbox-label">
                                <input type="checkbox" /> Leadership
                              </label>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="form-group">
                            <label>Recognition type:</label>
                            <select className="form-select">
                              <option>Above and Beyond</option>
                              <option>Team Player</option>
                              <option>Innovation</option>
                              <option>Customer Focus</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Message:</label>
                            <textarea
                              className="form-textarea"
                              placeholder={`What did ${feedbackTarget.name} do that deserves recognition?`}
                              rows={3}
                            />
                          </div>
                          <div className="form-group">
                            <label className="checkbox-label">
                              <input type="checkbox" defaultChecked /> Make visible to team
                            </label>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="modal-actions">
                      <button className="action-btn secondary" onClick={() => setShowFeedbackModal(false)}>
                        Cancel
                      </button>
                      <button className="action-btn primary" onClick={submitFeedback}>
                        {feedbackType === 'request' ? 'Send Request' : 'Send Recognition'}
                      </button>
                    </div>

                    <div className="time-saved-hint">
                      <span className="hint-icon">⏱️</span>
                      {feedbackType === 'request'
                        ? 'Automated request saves ~5 min vs manual emails'
                        : 'Instant recognition saves ~3 min vs separate system'}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Review Modal */}
            <AnimatePresence>
              {showReviewModal && reviewTarget && (
                <motion.div
                  className="modal-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowReviewModal(false)}
                >
                  <motion.div
                    className="modal-content review-modal"
                    initial={{ scale: 0.9, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className="modal-close" onClick={() => setShowReviewModal(false)}>×</button>

                    <h2 className="modal-title">Performance Review</h2>
                    <p className="modal-subtitle">Complete review for {reviewTarget.name} ({reviewTarget.role})</p>

                    <div className="review-form">
                      <div className="form-group">
                        <label>Overall Rating</label>
                        <div className="rating-buttons">
                          <button className="rating-btn exceeds">Exceeds</button>
                          <button className="rating-btn meets active">Meets</button>
                          <button className="rating-btn developing">Developing</button>
                          <button className="rating-btn needs">Needs Improvement</button>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Key Accomplishments</label>
                        <textarea
                          className="form-textarea"
                          placeholder="What were this person's most significant contributions?"
                          rows={3}
                          defaultValue={reviewTarget.pastReviews[0]?.summary || ''}
                        />
                      </div>

                      <div className="form-group">
                        <label>Development Areas</label>
                        <textarea
                          className="form-textarea"
                          placeholder="What skills should they focus on developing?"
                          rows={2}
                        />
                      </div>

                      <div className="form-group">
                        <label>Goals for Next Period</label>
                        <textarea
                          className="form-textarea"
                          placeholder="What should they accomplish in the next review period?"
                          rows={2}
                        />
                      </div>

                      <div className="ai-assist">
                        <div className="ai-header">
                          <span className="ai-icon">✨</span>
                          <span className="ai-label">AI-Assisted Suggestions</span>
                        </div>
                        <div className="ai-suggestions">
                          <p>Based on {reviewTarget.name}'s signals and peer feedback:</p>
                          <ul>
                            {reviewTarget.signals.map((signal, i) => (
                              <li key={i}>{signal.label}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="modal-actions">
                      <button className="action-btn secondary" onClick={() => setShowReviewModal(false)}>
                        Save Draft
                      </button>
                      <button className="action-btn primary" onClick={submitReview}>
                        Submit Review
                      </button>
                    </div>

                    <div className="time-saved-hint">
                      <span className="hint-icon">⏱️</span>
                      AI-assisted review saves ~15 min vs blank form
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            <footer className="footer">
              <p>
                Built by <strong>Elijah Bowie</strong> to demonstrate talent management product capabilities.
                <span className="footer-divider">|</span>
                <a href="mailto:elijahbowie@gmail.com">elijahbowie@gmail.com</a>
                <span className="footer-divider">|</span>
                <a href="https://github.com/elijahbowie" target="_blank" rel="noopener noreferrer">GitHub</a>
              </p>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default App
